import {type FC, useCallback, useEffect, useRef, useState} from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {keyframes, styled} from 'styled-components';

import {type SupportedLanguage} from '@shared/models/language';

import {useResponsive} from '@shared_web/breakpoints';
import {LoadingIndicator} from '@shared_web/components/core/loading_indicator';
import {ZINDEX} from '@shared_web/lib/zindex';

import {translate} from '@mavismart_frontend/components/translate';
import {useAgentContext} from '@mavismart_frontend/hooks/use_agent_context';
import {MAIN_COLOR, TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';
import {
  type AgentMessage,
  type AgentMetadata,
  type AgentSource,
  type AgentContext,
  type ModelTier,
  reset,
  sendMessage,
  setModelTier,
  togglePanel,
  useAgentIsLoading,
  useAgentMessages,
  useAgentModelTier,
  useAgentPanelOpen,
} from '@mavismart_frontend/stores/agent_store';

// Panel dimensions
const PANEL_WIDTH = 520;

export const AgentPanel: FC = () => {
  const panelOpen = useAgentPanelOpen();
  const messages = useAgentMessages();
  const modelTier = useAgentModelTier();
  const isLoading = useAgentIsLoading();
  const {isMobile} = useResponsive();
  const context = useAgentContext();
  const account = useAccount();
  const lang = getUserLanguage(account);

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  // biome-ignore lint/correctness/useExhaustiveDependencies: messages and isLoading are intentional triggers
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [messages, isLoading]);

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed.length === 0 || isLoading) return;
    setInputValue('');
    void sendMessage(trimmed, context);
  }, [inputValue, isLoading, context]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleModelChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setModelTier(e.target.value as ModelTier);
  }, []);

  const handleReset = useCallback(() => {
    reset();
  }, []);

  const handleBackdropClick = useCallback(() => {
    togglePanel();
  }, []);

  if (!panelOpen) return null;

  return (
    <>
      {!isMobile && <Backdrop onClick={handleBackdropClick} />}
      <PanelContainer $isMobile={isMobile}>
        {/* Header */}
        <PanelHeader>
          <HeaderTitle>{translate('agent__title', lang)}</HeaderTitle>
          <HeaderActions>
            <ResetButton onClick={handleReset} title={translate('agent__reset_title', lang)}>
              {translate('agent__reset', lang)}
            </ResetButton>
            <CloseButton onClick={handleBackdropClick} title={translate('agent__close', lang)}>
              ✕
            </CloseButton>
          </HeaderActions>
        </PanelHeader>

        {/* Model selector */}
        <ModelSelector>
          <ModelLabel>{translate('agent__model_label', lang)}</ModelLabel>
          <ModelSelect value={modelTier} onChange={handleModelChange}>
            <option value="fast">{translate('agent__model_fast', lang)}</option>
            <option value="medium">{translate('agent__model_medium', lang)}</option>
            <option value="slow">{translate('agent__model_slow', lang)}</option>
          </ModelSelect>
        </ModelSelector>

        {/* Messages */}
        <MessagesContainer>
          {messages.length === 0 && !isLoading && (
            <WelcomeMessage>{translate('agent__welcome', lang)}</WelcomeMessage>
          )}
          {messages.map(msg => (
            <MessageItem key={msg.timestamp} message={msg} />
          ))}
          {isLoading && <TypingIndicator lang={lang} />}
          <div ref={messagesEndRef} />
        </MessagesContainer>

        {/* Input */}
        <InputFooter>
          <ContextChips context={context} lang={lang} />
          <InputContainer>
            <InputArea
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={translate('agent__placeholder', lang)}
              rows={1}
              disabled={isLoading}
            />
            <SendButton
              onClick={handleSend}
              disabled={inputValue.trim().length === 0 || isLoading}
              title={translate('agent__send', lang)}
            >
              ➤
            </SendButton>
          </InputContainer>
        </InputFooter>
      </PanelContainer>
    </>
  );
};
AgentPanel.displayName = 'AgentPanel';

// --- Sub-components ---

const MessageItem: FC<{message: AgentMessage}> = ({message}) => {
  const isUser = message.role === 'user';
  const isError = message.isError === true;

  return (
    <MessageWrapper $isUser={isUser}>
      <MessageBubble $isUser={isUser} $isError={isError}>
        {isUser ? message.content : <MarkdownContent content={message.content} />}
      </MessageBubble>
      {!(isUser || isError) && message.sources && message.sources.length > 0 && (
        <SourcesBadges sources={message.sources} />
      )}
      {!(isUser || isError) && message.metadata && (
        <MetadataAccordion metadata={message.metadata} />
      )}
    </MessageWrapper>
  );
};

const remarkPlugins = [remarkGfm];

const MarkdownContent: FC<{content: string}> = ({content}) => (
  <MarkdownWrapper>
    <Markdown
      remarkPlugins={remarkPlugins}
      components={{
        a: ({href, children}) => (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
      }}
    >
      {content}
    </Markdown>
  </MarkdownWrapper>
);

const SourcesBadges: FC<{sources: AgentSource[]}> = ({sources}) => {
  const account = useAccount();
  const lang = getUserLanguage(account);

  return (
    <SourcesContainer>
      {sources.map(source => (
        <SourceBadge key={`${source.type}-${source.name}-${source.page ?? ''}`} $type={source.type}>
          <SourceIcon>{source.type === 'document' ? '\u{1F4C4}' : '\u{1F50C}'}</SourceIcon>
          <SourceName>{source.name}</SourceName>
          {source.page && (
            <SourceDetail>
              {translate('agent__source_page', lang, {page: source.page})}
            </SourceDetail>
          )}
        </SourceBadge>
      ))}
    </SourcesContainer>
  );
};

const MetadataAccordion: FC<{metadata: AgentMetadata}> = ({metadata}) => {
  const [open, setOpen] = useState(false);
  const account = useAccount();
  const lang = getUserLanguage(account);

  const handleToggle = useCallback(() => setOpen(v => !v), []);

  return (
    <AccordionWrapper>
      <AccordionHeader onClick={handleToggle}>
        <AccordionArrow $open={open}>▸</AccordionArrow>
        <AccordionTitle>
          {metadata.tokens.total} tokens · {formatDuration(metadata.durationMs)} ·{' '}
          {formatCost(metadata.estimatedCostUsd)}
        </AccordionTitle>
      </AccordionHeader>
      {open && (
        <AccordionContent>
          <MetaRow>
            <MetaLabel>{translate('agent__meta_model', lang)}</MetaLabel>
            <MetaValue>{metadata.modelId}</MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaLabel>Tokens</MetaLabel>
            <MetaValue>
              {metadata.tokens.input} in / {metadata.tokens.output} out / {metadata.tokens.total}{' '}
              total
            </MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaLabel>{translate('agent__meta_estimated_cost', lang)}</MetaLabel>
            <MetaValue>{formatCost(metadata.estimatedCostUsd)}</MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaLabel>{translate('agent__meta_total_duration', lang)}</MetaLabel>
            <MetaValue>{formatDuration(metadata.durationMs)}</MetaValue>
          </MetaRow>
          {metadata.toolsUsed.length > 0 && (
            <MetaRow>
              <MetaLabel>Tools</MetaLabel>
              <ToolsList>
                {metadata.toolsUsed.map(tool => (
                  <ToolItem key={`${tool.name}-${tool.durationMs}`}>
                    {tool.name} ({formatDuration(tool.durationMs)})
                  </ToolItem>
                ))}
              </ToolsList>
            </MetaRow>
          )}
        </AccordionContent>
      )}
    </AccordionWrapper>
  );
};

const TypingIndicator: FC<{lang: SupportedLanguage}> = ({lang}) => (
  <TypingWrapper>
    <LoadingIndicator size={20} thickness={2} color={MAIN_COLOR} />
    <TypingText>{translate('agent__thinking', lang)}</TypingText>
  </TypingWrapper>
);

/**
 * Displays small contextual chips above the input area so the user knows which
 * page/door/group the agent will see. Only shows chips for values that are
 * actually present in the current navigation context.
 */
const ContextChips: FC<{context: AgentContext; lang: SupportedLanguage}> = ({context, lang}) => {
  const chips: Array<{label: string; value: string}> = [];

  chips.push({label: translate('agent__context_page', lang), value: context.pageName});

  if (context.doorId) {
    chips.push({label: translate('agent__context_door', lang), value: context.doorId});
  }
  if (context.groupId) {
    chips.push({label: translate('agent__context_group', lang), value: context.groupId});
  }

  return (
    <ChipsRow>
      {chips.map(chip => (
        <ContextChip key={chip.label}>
          <ChipLabel>{chip.label}</ChipLabel>
          <ChipValue>{chip.value}</ChipValue>
        </ContextChip>
      ))}
    </ChipsRow>
  );
};

// --- Helpers ---

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatCost(usd: number): string {
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(3)}`;
}

// --- Styled components ---

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: ${ZINDEX.Modal - 1};
`;

const PanelContainer = styled.div<{$isMobile: boolean}>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${p => (p.$isMobile ? '100%' : `${PANEL_WIDTH}px`)};
  background: #ffffff;
  z-index: ${ZINDEX.Modal};
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.12);
  animation: ${slideIn} 0.2s ease-out;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
`;

const HeaderTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${TEXT_MAIN_COLOR};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResetButton = styled.button`
  background: none;
  border: 1px solid #dadada;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: ${TEXT_MAIN_COLOR};
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  color: ${TEXT_MAIN_COLOR};
  cursor: pointer;
  padding: 4px 8px;
  &:hover {
    color: ${MAIN_COLOR};
  }
`;

const ModelSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
`;

const ModelLabel = styled.span`
  font-size: 12px;
  color: ${TEXT_MAIN_COLOR};
`;

const ModelSelect = styled.select`
  font-size: 12px;
  padding: 4px 24px 4px 8px;
  border: 1px solid #dadada;
  border-radius: 6px;
  color: ${TEXT_MAIN_COLOR};
  background-color: white;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg fill='none' stroke='%23677' stroke-width='2' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 4px center;
  background-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${MAIN_COLOR};
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WelcomeMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 14px;
  margin-top: 40px;
`;

const MessageWrapper = styled.div<{$isUser: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: ${p => (p.$isUser ? 'flex-end' : 'flex-start')};
  gap: 4px;
`;

const MessageBubble = styled.div<{$isUser: boolean; $isError: boolean}>`
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  white-space: ${p => (p.$isUser ? 'pre-wrap' : 'normal')};
  word-break: break-word;
  ${p =>
    p.$isUser
      ? `
    background: ${MAIN_COLOR};
    color: white;
    border-bottom-right-radius: 4px;
  `
      : p.$isError
        ? `
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
    border-bottom-left-radius: 4px;
  `
        : `
    background: #f2f3f5;
    color: ${TEXT_MAIN_COLOR};
    border-bottom-left-radius: 4px;
  `}
`;

// Markdown content styles

const MarkdownWrapper = styled.div`
  p {
    margin: 0;
  }
  p + p {
    margin-top: 4px;
  }
  br {
    display: block;
    content: '';
    margin-top: 4px;
  }
  h1, h2, h3, h4, h5, h6 {
    margin: 8px 0 4px;
    font-weight: 600;
    line-height: 1.3;
  }
  h1 { font-size: 1.2em; }
  h2 { font-size: 1.1em; }
  h3 { font-size: 1.05em; }
  strong {
    font-weight: 600;
  }
  em {
    font-style: italic;
  }
  del {
    text-decoration: line-through;
  }
  code {
    background: rgba(0, 0, 0, 0.06);
    border-radius: 3px;
    padding: 1px 4px;
    font-family: monospace;
    font-size: 12px;
  }
  pre {
    background: #1e1e1e;
    color: #d4d4d4;
    border-radius: 6px;
    padding: 10px 12px;
    overflow-x: auto;
    margin: 6px 0;
    code {
      background: none;
      padding: 0;
      color: inherit;
      font-size: 12px;
    }
  }
  ul, ol {
    margin: 4px 0;
    padding-left: 20px;
  }
  li {
    margin: 2px 0;
  }
  li input[type='checkbox'] {
    margin-right: 6px;
  }
  table {
    border-collapse: collapse;
    margin: 6px 0;
    font-size: 13px;
    width: 100%;
  }
  th, td {
    border: 1px solid #ddd;
    padding: 4px 8px;
    text-align: left;
  }
  th {
    background: rgba(0, 0, 0, 0.04);
    font-weight: 600;
  }
  blockquote {
    margin: 4px 0;
    padding-left: 10px;
    border-left: 3px solid #ddd;
    color: #666;
  }
  hr {
    border: none;
    border-top: 1px solid #e8e8e8;
    margin: 8px 0;
  }
  a {
    color: ${MAIN_COLOR};
    text-decoration: underline;
    &:hover {
      opacity: 0.8;
    }
  }
`;

// Source badges

const SourcesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 85%;
  margin-top: 4px;
`;

const SourceBadge = styled.span<{$type: 'document' | 'api'}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  background: ${p => (p.$type === 'document' ? '#e8f4fd' : '#f0e8fd')};
  color: ${p => (p.$type === 'document' ? '#1a6fa3' : '#6b3fa0')};
  border: 1px solid ${p => (p.$type === 'document' ? '#b8ddf0' : '#d4b8f0')};
`;

const SourceIcon = styled.span`
  font-size: 12px;
`;

const SourceName = styled.span`
  font-weight: 500;
`;

const SourceDetail = styled.span`
  opacity: 0.7;
  &::before {
    content: '\u00B7';
    margin-right: 2px;
  }
`;

// Accordion (metadata)

const AccordionWrapper = styled.div`
  max-width: 85%;
  margin-top: 2px;
`;

const AccordionHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  font-size: 11px;
  color: #999;

  &:hover {
    color: ${TEXT_MAIN_COLOR};
  }
`;

const AccordionArrow = styled.span<{$open: boolean}>`
  display: inline-block;
  transition: transform 0.15s ease;
  transform: ${p => (p.$open ? 'rotate(90deg)' : 'rotate(0deg)')};
`;

const AccordionTitle = styled.span`
  font-size: 11px;
`;

const AccordionContent = styled.div`
  padding: 6px 8px;
  margin-top: 2px;
  border-radius: 6px;
  background: #f9f9f9;
  border: 1px solid #eee;
  font-size: 11px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: baseline;
`;

const MetaLabel = styled.span`
  color: #999;
  min-width: 80px;
  flex-shrink: 0;
`;

const MetaValue = styled.span`
  color: ${TEXT_MAIN_COLOR};
`;

const ToolsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToolItem = styled.span`
  color: ${TEXT_MAIN_COLOR};
  font-family: monospace;
  font-size: 10px;
`;

// Typing indicator

const TypingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
`;

const TypingText = styled.span`
  font-size: 13px;
  color: #999;
  font-style: italic;
`;

// Input area

const InputFooter = styled.div`
  border-top: 1px solid #e8e8e8;
  flex-shrink: 0;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 16px 0;
`;

const ContextChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  background: #f2f3f5;
  border: 1px solid #e8e8e8;
  color: ${TEXT_MAIN_COLOR};
`;

const ChipLabel = styled.span`
  color: #999;
`;

const ChipValue = styled.span`
  font-weight: 500;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px;
  flex-shrink: 0;
`;

const InputArea = styled.textarea`
  flex: 1;
  border: 1px solid #dadada;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  min-height: 40px;
  max-height: 120px;
  color: ${TEXT_MAIN_COLOR};

  &:focus {
    outline: none;
    border-color: ${MAIN_COLOR};
  }

  &::placeholder {
    color: #bbb;
  }

  &:disabled {
    background: #f9f9f9;
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${MAIN_COLOR};
  color: white;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    background: #dadada;
    cursor: not-allowed;
  }
`;
